<template>
  <section id="printable">
    <div class="is-flex is-justify-content-space-between is-align-items-center">
      <span class="has-text-weight-semibold is-flex is-flex-wrap-wrap">{{
        getPaginationDetails
      }}</span>
      <span
        class="divider is-vertical has-background-color-7 m-0 is-hidden-tablet"
      ></span>
      <div class="is-flex is-align-items-center">
        <span class="has-text-weight-semibold mr-10">
          {{ General.ORDER_BY }}
        </span>
        <b-dropdown
          v-model="sortField"
          aria-role="list"
          :mobileModal="false"
          position="is-bottom-left"
        >
          <b-button
            slot="trigger"
            type="is-light"
            iconRight="chevron-down"
            outlined
          >
            <span>{{ sortField.name }}</span>
          </b-button>
          <b-dropdown-item
            v-for="(filter, index) in filterKeys"
            :key="index"
            :value="filter"
            aria-role="listitem"
          >
            <span class="has-text-weight-semibold">{{ filter.name }}</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
    <section v-if="processes.count === 0">
      <NoListMessage />
    </section>
    <routerLink
      v-for="(process, index) in processes.list"
      :key="process.id"
      class="list-container"
      role="link"
      rel="search external"
      :target="isNewTab(process) ? '_blank' : ''"
      :to="getRedirectLink(process)"
    >
      <div class="card">
        <hr v-if="index == 0" />
        <h1 class="nup-data is-size-5 has-text-weight-semibold">
          {{ `${General.NUP} ${applyMaskNUP(process.nup)}` }}
        </h1>
        <div class="columns is-justify-content-space-between">
          <div class="column is-7">
            <span class="is-size-6 has-text-weight-normal">
              {{ `${General.OPENING_DATE}: `
              }}{{ new Date(process.date) | dateFormat("DD/MM/YYYY") }}
            </span>
            <br />
            <span class="is-size-6 has-text-weight-normal">
              {{ General.SUBJECT }}: {{ process.subject_name }}
            </span>
            <br />
            <span
              v-if="process.cc.length"
              class="is-size-6 has-text-weight-normal"
            >
              {{ General.INTERESTED_PLURAL }}:
              <span
                v-for="(capacity, capacityIndex) in process.cc"
                :key="capacityIndex"
              >
                {{ getCCFormated(capacity)
                }}{{ process.cc.length - 1 > capacityIndex ? ", " : "." }}
              </span>
            </span>
            <span
              v-if="process.history_search && process.history_search.length"
              class="is-size-6 has-text-weight-normal"
            >
              {{ General.CURRENT_STATUS }}:
              {{ process.status || process.history_search[0].status }}
            </span>
          </div>
          <div class="column is-4">
            <div class="is-flex is-flex-direction-column">
              <b-tooltip
                v-if="
                  process.process_reference && process.process_reference.length
                "
                class="process-reference-container is-size-6 has-text-weight-normal mb-10"
                :active="showReferenceTooltip(process.process_reference)"
                :label="tooltipText(process.process_reference)"
                type="is-blue-oil"
                multilined
              >
                <span class="has-text-weight-semibold">
                  {{ App.ProcessOpeningScreen.PROCESS_REFERENCE }}:
                </span>
                <span
                  v-for="(reference, referenceIndex) in reducedReferenceList(
                    process.process_reference
                  )"
                  :key="referenceIndex"
                >
                  {{ applyMaskNUP(reference.nup)
                  }}{{
                    process.process_reference.length - 1 > referenceIndex
                      ? ", "
                      : "."
                  }}
                </span>
                <span class="and-others">
                  {{
                    showReferenceTooltip(process.process_reference)
                      ? `${General.TEXTS.AND_OTHERS.toLowerCase()}.`
                      : ""
                  }}
                </span>
              </b-tooltip>
              <span v-if="process.reference_number">
                <span class="has-text-weight-semibold">
                  {{ App.ProcessOpeningScreen.PROTOCOL_REFERENCE }}:
                </span>
                {{ process.reference_number }}.
              </span>
            </div>
          </div>
        </div>
        <hr />
      </div>
    </routerLink>
    <div
      class="is-flex mb-40"
      :class="{ 'is-flex-direction-column-reverse': isMobile }"
    >
      <b-dropdown
        v-model="currentPerPage"
        aria-role="list"
        position="is-top-right"
        :expanded="isMobile"
      >
        <template slot="trigger">
          <b-button class="custom-dropdown-button" :expanded="isMobile">
            {{ currentPerPage }} {{ General.PER_PAGE.toLowerCase() }}

            <b-icon class="spacing-mx-5" icon="chevron-down" />
          </b-button>
        </template>
        <div class="spacing-p-5 has-text-centered">
          {{ General.RECORDS_FOR_PAGE }}
          <div class="list-page">
            <b-dropdown-item
              v-for="option in [5, 10, 25]"
              :key="option"
              :value="option"
            >
              <div>
                {{ option }}
              </div>
            </b-dropdown-item>
          </div>
        </div>
      </b-dropdown>
      <div class="custom-pagination-container" :class="{ 'mb-10': isMobile }">
        <b-pagination
          :total="processes.count"
          :perPage="perPage"
          :current.sync="page"
          rangeBefore="1"
          rangeAfter="1"
          order="is-centered"
          rounded
          :iconPrev="prevIcon"
          :iconNext="nextIcon"
          :aria-next-label="General.NEXT_PAGE"
          :aria-previous-label="General.PREVIOUS_PAGE"
          :aria-page-label="General.PAGE"
          :aria-current-label="General.CURRENT_PAGE"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { App, General } from "@/utils/constants";
import { NUP, applyMask } from "@/utils/protocols";
import NoListMessage from "@/components/NoListMessage";
import { formatPagination, getCCFormated } from "@/utils/transformers";

export default {
  name: "SearchView",

  components: {
    NoListMessage
  },

  props: {
    processes: {
      type: Object,
      default: () => {}
    },
    onSelect: {
      type: Function,
      default: () => null
    },
    currentPage: {
      type: Number,
      default: 1
    },
    setCurrentPage: {
      type: Function,
      default: () => null
    },
    setOrderBy: {
      type: Function,
      default: () => null
    },
    isExternal: {
      type: Boolean,
      default: false
    },
    perPage: {
      type: Number,
      default: 5
    },
    setPerPage: {
      type: Function,
      default: () => null
    }
  },

  data() {
    return {
      App,
      General,
      isLoading: true,
      page: this.currentPage,
      sortField: { key: "date", name: General.DATE, order: "desc" },
      prevIcon: "chevron-left",
      nextIcon: "chevron-right",
      currentPerPage: this.perPage,
      filterKeys: [
        { key: "date", name: General.DATE, order: "desc" },
        { key: "nup", name: General.NUP, order: "asc" },
        { key: "subject_name", name: General.SUBJECT, order: "asc" }
      ],
      getCCFormated
    };
  },

  computed: {
    ...mapGetters({
      isMobile: "isMobile"
    }),
    getPaginationDetails() {
      return formatPagination(this.processes, this.currentPage, this.perPage);
    }
  },

  watch: {
    page: {
      handler() {
        this.setCurrentPage(this.page);
      }
    },
    sortField: {
      handler() {
        this.setOrderBy(this.sortField);
      }
    },
    currentPage(value) {
      this.page = value;
    },
    perPage(value) {
      this.currentPerPage = value;
      this.setPerPage(this.currentPerPage);
    },
    currentPerPage(value) {
      this.setPerPage(value);
    }
  },

  methods: {
    getDueDate(date) {
      const dueDate = new Date(date);
      const dateNow = new Date();
      const millisecondsPerDay = 86400 * 1000;

      const diff = dueDate - dateNow;
      return Math.ceil(diff / millisecondsPerDay);
    },

    applyMaskNUP(value) {
      return applyMask(NUP, value);
    },

    reducedReferenceList(referenceList) {
      return referenceList.slice(0, 3);
    },

    showReferenceTooltip(referenceList) {
      return referenceList.length > 3;
    },

    tooltipText(referenceList) {
      return referenceList.reduce((acc, actual, index) => {
        if (index === referenceList.length - 1)
          return acc + this.applyMaskNUP(actual.nup);
        else return acc + `${this.applyMaskNUP(actual.nup)}; `;
      }, "");
    },

    getRedirectLink(process) {
      if (this.isExternal)
        return {
          path: `/consultar-processo/${process.nup}`
        };
      else return this.onSelect(process).url;
    },

    isNewTab(process) {
      return this.onSelect(process)?.newTab;
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables";

.list-container {
  display: flex;
  flex-direction: column;
  max-height: 700px;

  .nup-data {
    margin-bottom: 3px;
  }

  hr {
    border: 1px solid $border-color;
  }
}
.card {
  display: flex;
  flex-direction: column;
  background-color: $color-1;
  color: $color-7;
  box-shadow: none;
  cursor: pointer;
  &:hover {
    .nup-data {
      color: $highligh-color;
    }
  }
}
.due-date-label {
  font-size: 12px;
  color: $orange;
}

.pagination {
  min-width: 30%;
  margin: 0 auto;
}

.process-reference-container:hover {
  .and-others {
    color: $color-12;
    text-decoration: underline;
  }
}
.divider.is-vertical {
  @media screen and (max-width: 400px) {
    display: none;
  }
}
</style>
